import React from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Badge } from 'primereact/badge';

/**
 * Plan Card Component
 * Displays a subscription plan in card format with pricing and features
 * Follows PrimeReact Diamond template styling
 *
 * @param {Object} plan - Subscription plan data
 * @param {boolean} isCurrentPlan - Whether this is the user's current plan
 * @param {Function} onSelect - Callback when plan is selected
 * @param {boolean} loading - Loading state for select button
 */
const PlanCard = ({ plan, isCurrentPlan = false, onSelect, loading = false }) => {
    const formatPrice = (price) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0,
        }).format(price);
    };

    const isPremium = plan.plan_code === 'PREMIUM' || plan.plan_code === 'ENTERPRISE';
    const isFree = plan.plan_code === 'FREE' || plan.price_monthly === 0;

    const cardHeader = (
        <div className="p-4 bg-primary text-primary-inverse border-round-top">
            <div className="flex justify-content-between align-items-center">
                <h3 className="m-0">{plan.name}</h3>
                {isPremium && (
                    <Badge value="POPULAR" severity="warning" />
                )}
                {isCurrentPlan && (
                    <Badge value="CURRENT" severity="success" />
                )}
            </div>
            {plan.description && (
                <p className="mt-2 mb-0 text-sm opacity-90">{plan.description}</p>
            )}
        </div>
    );

    return (
        <Card
            header={cardHeader}
            className={`h-full ${isPremium ? 'border-primary border-2' : ''} ${isCurrentPlan ? 'surface-100' : ''}`}
        >
            <div className="flex flex-column h-full">
                {/* Pricing Section */}
                <div className="text-center mb-4 pb-4 border-bottom-1 border-200">
                    <div className="text-6xl font-bold text-primary mb-2">
                        {isFree ? 'Free' : formatPrice(plan.price_monthly)}
                    </div>
                    {!isFree && (
                        <div className="text-600">
                            per month
                            {plan.price_annual && (
                                <div className="text-sm mt-1">
                                    or {formatPrice(plan.price_annual)}/year
                                    <span className="text-green-500 ml-2">Save {Math.round((1 - (plan.price_annual / (plan.price_monthly * 12))) * 100)}%</span>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Plan Highlights */}
                <div className="mb-4 pb-4 border-bottom-1 border-200">
                    <div className="grid">
                        <div className="col-6">
                            <div className="text-center p-2">
                                <i className="pi pi-wallet text-blue-500 mb-1" style={{ fontSize: '1.5rem' }}></i>
                                <div className="text-xs text-600">Credits</div>
                                <div className="font-semibold">{plan.credits_included?.toLocaleString() || '∞'}</div>
                            </div>
                        </div>
                        <div className="col-6">
                            <div className="text-center p-2">
                                <i className="pi pi-star text-green-500 mb-1" style={{ fontSize: '1.5rem' }}></i>
                                <div className="text-xs text-600">Features</div>
                                <div className="font-semibold">{plan.feature_count || plan.features?.length || 0}</div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Features List */}
                <div className="flex-1">
                    <h4 className="mt-0 mb-3">What's included:</h4>
                    <ul className="list-none p-0 m-0">
                        {plan.features && plan.features.length > 0 ? (
                            plan.features.slice(0, 8).map((feature, index) => (
                                <li key={index} className="flex align-items-start mb-2">
                                    <i className="pi pi-check text-green-500 mr-2 mt-1"></i>
                                    <div className="flex-1">
                                        <div className="text-sm">{feature.display_name}</div>
                                        {!feature.is_unlimited && feature.usage_limit && (
                                            <div className="text-xs text-600">Up to {feature.usage_limit}/month</div>
                                        )}
                                    </div>
                                </li>
                            ))
                        ) : (
                            <li className="text-600 text-sm">No features information</li>
                        )}
                        {plan.features && plan.features.length > 8 && (
                            <li className="text-600 text-sm mt-2">
                                + {plan.features.length - 8} more features
                            </li>
                        )}
                    </ul>
                </div>

                {/* Action Button */}
                <div className="mt-4">
                    {isCurrentPlan ? (
                        <Button
                            label="Current Plan"
                            icon="pi pi-check"
                            className="w-full p-button-outlined"
                            disabled
                        />
                    ) : (
                        <Button
                            label={isFree ? "Downgrade to Free" : "Upgrade Plan"}
                            icon="pi pi-arrow-right"
                            className={`w-full ${isPremium ? 'p-button-success' : ''}`}
                            onClick={() => onSelect(plan)}
                            loading={loading}
                        />
                    )}
                </div>
            </div>
        </Card>
    );
};

export default PlanCard;
